import { useCallback, useEffect, useRef, useState } from "react";
import {
  createDesignCalendarAccion,
  createDesignCalendarNota,
  deleteDesignCalendarAccion,
  deleteDesignCalendarNota,
  updateDesignCalendarAccion,
  updateDesignCalendarCliente,
  updateDesignCalendarNota,
} from "../../api/DesignCalendar/designCalendarApi";
import {
  applyPendingOpsToSnapshot,
  isBrowserOnline,
  isNetworkError,
  isTempId,
  remapTempIdInOps,
  shouldQueueOfflineSave,
} from "./designCalendarOfflinePure";
import {
  clearPendingOps,
  enqueueYearOp,
  getMergedYearSnapshot,
  getPendingOps,
  getYearRecord,
  putYearSnapshot,
  setPendingOps,
} from "./designCalendarOfflineStore";

/**
 * Offline completo del Design Calendar: cache por año + cola CRUD + flush.
 */
export default function useDesignCalendarOffline(year) {
  const [isOnline, setIsOnline] = useState(() => isBrowserOnline());
  const [pendingCount, setPendingCount] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [connectivityNotice, setConnectivityNotice] = useState("");
  const flushingRef = useRef(false);
  const yearRef = useRef(year);
  yearRef.current = year;

  const refreshPendingCount = useCallback(async (anio = yearRef.current) => {
    try {
      const ops = await getPendingOps(anio);
      setPendingCount(ops.length);
      return ops.length;
    } catch {
      setPendingCount(0);
      return 0;
    }
  }, []);

  useEffect(() => {
    const onOnline = () => {
      setIsOnline(true);
      setConnectivityNotice("");
    };
    const onOffline = () => {
      setIsOnline(false);
      setConnectivityNotice("offline");
    };
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    refreshPendingCount(year);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, [year, refreshPendingCount]);

  const cacheSnapshot = useCallback(
    async (anio, data, opts) => {
      try {
        await putYearSnapshot(anio, data, opts);
        await refreshPendingCount(anio);
      } catch {
        /* ignore IDB */
      }
    },
    [refreshPendingCount],
  );

  const loadCachedYear = useCallback(async (anio) => {
    try {
      return await getMergedYearSnapshot(anio);
    } catch {
      return null;
    }
  }, []);

  const hasPending = useCallback(async (anio = yearRef.current) => {
    try {
      const ops = await getPendingOps(anio);
      return ops.length > 0;
    } catch {
      return false;
    }
  }, []);

  const queueOp = useCallback(
    async (anio, op) => {
      await enqueueYearOp(anio, op);
      const n = await refreshPendingCount(anio);
      if (!isBrowserOnline()) setConnectivityNotice("offline");
      else if (n > 0) setConnectivityNotice("pending");
      return n;
    },
    [refreshPendingCount],
  );

  const shouldQueue = useCallback((networkError = false) => {
    return shouldQueueOfflineSave({
      online: isBrowserOnline(),
      offlineEnabled: true,
      networkError,
    });
  }, []);

  const flushPending = useCallback(async (anio = yearRef.current) => {
    if (flushingRef.current) return { ok: false, reason: "busy" };
    if (!isBrowserOnline()) return { ok: false, reason: "offline" };
    flushingRef.current = true;
    setSyncing(true);
    setConnectivityNotice("syncing");
    try {
      let ops = await getPendingOps(anio);
      if (!ops.length) {
        setConnectivityNotice("");
        setPendingCount(0);
        return { ok: true, flushed: 0 };
      }
      while (ops.length) {
        const [op, ...rest] = ops;
        try {
          if (op.type === "nota.create") {
            const created = await createDesignCalendarNota(op.payload);
            ops = remapTempIdInOps(rest, op.tempId, created.Id);
            await setPendingOps(anio, ops);
          } else if (op.type === "nota.update") {
            if (isTempId(op.id)) {
              // still waiting for create remap — skip to end? keep and break
              await setPendingOps(anio, ops);
              break;
            }
            await updateDesignCalendarNota(op.id, op.payload);
            ops = rest;
            await setPendingOps(anio, ops);
          } else if (op.type === "nota.delete") {
            if (isTempId(op.id)) {
              // delete of never-synced create: drop matching create if any
              ops = rest.filter(
                (item) =>
                  !(
                    item.type === "nota.create" &&
                    Number(item.tempId) === Number(op.id)
                  ) && Number(item.id) !== Number(op.id),
              );
              await setPendingOps(anio, ops);
            } else {
              await deleteDesignCalendarNota(op.id);
              ops = rest;
              await setPendingOps(anio, ops);
            }
          } else if (op.type === "accion.create") {
            const created = await createDesignCalendarAccion(op.payload);
            ops = remapTempIdInOps(rest, op.tempId, created.Id);
            await setPendingOps(anio, ops);
          } else if (op.type === "accion.update") {
            if (isTempId(op.id)) {
              await setPendingOps(anio, ops);
              break;
            }
            await updateDesignCalendarAccion(op.id, op.payload);
            ops = rest;
            await setPendingOps(anio, ops);
          } else if (op.type === "accion.delete") {
            if (isTempId(op.id)) {
              ops = rest.filter(
                (item) =>
                  !(
                    item.type === "accion.create" &&
                    Number(item.tempId) === Number(op.id)
                  ) && Number(item.id) !== Number(op.id),
              );
              await setPendingOps(anio, ops);
            } else {
              await deleteDesignCalendarAccion(op.id);
              ops = rest;
              await setPendingOps(anio, ops);
            }
          } else if (op.type === "cliente.update") {
            await updateDesignCalendarCliente(op.id, op.payload);
            ops = rest;
            await setPendingOps(anio, ops);
          } else {
            ops = rest;
            await setPendingOps(anio, ops);
          }
        } catch (err) {
          if (isNetworkError(err)) {
            setConnectivityNotice("offline");
            return { ok: false, reason: "network" };
          }
          // 4xx: keep op, stop flush
          setConnectivityNotice("pending");
          return { ok: false, reason: "conflict", error: err };
        }
      }
      const left = await getPendingOps(anio);
      setPendingCount(left.length);
      if (!left.length) {
        await clearPendingOps(anio);
        setConnectivityNotice("");
      } else {
        setConnectivityNotice("pending");
      }
      return { ok: left.length === 0, flushed: true, remaining: left.length };
    } finally {
      flushingRef.current = false;
      setSyncing(false);
    }
  }, []);

  /** Preview merge without IDB (for tests / local state sync). */
  const mergeLocal = useCallback((snapshot, ops) => {
    return applyPendingOpsToSnapshot(snapshot, ops);
  }, []);

  const readRecord = useCallback(async (anio) => getYearRecord(anio), []);

  return {
    isOnline,
    pendingCount,
    syncing,
    connectivityNotice,
    setConnectivityNotice,
    cacheSnapshot,
    loadCachedYear,
    hasPending,
    queueOp,
    shouldQueue,
    flushPending,
    refreshPendingCount,
    mergeLocal,
    readRecord,
  };
}
